import { useCallback, useEffect, useRef, useState } from 'react';
import PaidActionConfirm from './PaidActionConfirm';
import { betaAiPaused } from '../../utils/betaSpendPause';

/**
 * First click opens the estimate. The spend runs only after Confirm.
 * The dialog mounts after this click so the opening click cannot land
 * on Confirm. Unmount cancels. It does not spend.
 */
export function usePaidConfirm() {
  const [pending, setPending] = useState(null);
  const resolver = useRef(null);
  const openTimer = useRef(null);

  const finish = (ok) => {
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    const resolve = resolver.current;
    resolver.current = null;
    setPending(null);
    resolve?.(ok);
  };

  const ask = useCallback((actionId, extras = {}) => new Promise((resolve) => {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    resolver.current = resolve;
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null;
      if (betaAiPaused()) {
        setPending({ paused: true });
        return;
      }
      setPending({ actionId, ...extras });
    }, 0);
  }), []);

  useEffect(() => () => {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    const resolve = resolver.current;
    resolver.current = null;
    resolve?.(false);
  }, []);

  const dialog = pending?.paused ? (
    <PaidActionConfirm paused onCancel={() => finish(false)} />
  ) : pending ? (
    <PaidActionConfirm
      actionId={pending.actionId}
      inputTokens={pending.inputTokens}
      messageCount={pending.messageCount}
      discovery={pending.discovery}
      sizeMissing={Boolean(pending.sizeMissing)}
      draftCheck={pending.draftCheck}
      title={pending.title}
      hint={pending.hint}
      onCancel={() => finish(false)}
      onConfirm={() => finish(true)}
    />
  ) : null;

  return { ask, dialog };
}
